import { Badge, Table, type TableColumn } from '../../ui';
import {
  priorityMeta,
  statusLabel,
  statusTone,
} from '../../features/tasks/taskModel';
import type { Task } from '../../features/tasks/types';

export interface TaskListViewProps {
  tasks: Task[];
  loading?: boolean;
  onEdit: (task: Task) => void;
  /** Opens a task's detail page (row click). Falls back to onEdit. */
  onOpen?: (task: Task) => void;
}

/** Tabular list view of a room's tasks (click a row to open its page). */
export function TaskListView({ tasks, loading, onEdit, onOpen }: TaskListViewProps) {
  const columns: TableColumn<Task>[] = [
    {
      key: 'title',
      header: 'Task',
      render: (t) => (
        <div className="min-w-0">
          <p className="font-medium text-zinc-100">{t.title}</p>
          {t.description && (
            <p className="line-clamp-1 text-xs text-zinc-500">{t.description}</p>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (t) => <Badge tone={statusTone(t.status)}>{statusLabel(t.status)}</Badge>,
    },
    {
      key: 'priority',
      header: 'Priority',
      render: (t) => {
        const p = priorityMeta(t.priority);
        return p ? (
          <Badge tone={p.tone} dot>
            {p.label}
          </Badge>
        ) : (
          <span className="text-xs text-zinc-600">—</span>
        );
      },
    },
    {
      key: 'assignee',
      header: 'Assignee',
      render: (t) =>
        t.assignee ? (
          <span className="text-sm text-zinc-300">{t.assignee}</span>
        ) : (
          <span className="text-xs text-zinc-600">Unassigned</span>
        ),
    },
    {
      key: 'tags',
      header: 'Tags',
      render: (t) =>
        (t.tags ?? []).length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {(t.tags ?? []).map((tag) => (
              <Badge key={tag} tone="neutral">
                {tag}
              </Badge>
            ))}
          </div>
        ) : (
          <span className="text-xs text-zinc-600">—</span>
        ),
    },
  ];

  return (
    <Table
      columns={columns}
      rows={tasks}
      rowKey={(t) => t.id}
      loading={loading}
      onRowClick={onOpen ?? onEdit}
      empty="No tasks match the current filters."
    />
  );
}

export default TaskListView;
